/**
 * Iter 147 — Trends Board
 *
 * Layout
 * ------
 *   Tabs: All · Bookmarked · Ideas · Trending
 *   Left column of masonry cards (title, image, source, tags, brand chips)
 *   Right column: filters + "Add idea" CTA + workspace settings drawer
 *
 * Each card shows the AI-drafted content angle (headline + hook + first
 * caption) and exposes a "Push to brand" button. Bookmarking is a one-click
 * heart icon that toggles per-user membership.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Sparkles, Bookmark, BookmarkCheck, Plus, Search, TrendingUp, Rss, Settings, Send, RefreshCw, Trash2, ExternalLink, Wand2, Zap, Layers } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const TABS = [
  { key: "all", label: "All", icon: Layers },
  { key: "featured", label: "Featured today", icon: Sparkles },
  { key: "bookmarked", label: "Bookmarked", icon: BookmarkCheck },
  { key: "ideas", label: "Ideas", icon: Wand2 },
  { key: "trending", label: "Trending", icon: TrendingUp },
];

export default function Trends() {
  const { activeWorkspaceId } = useAuth();
  const { can } = useWorkspacePerms(activeWorkspaceId);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [brands, setBrands] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pushFor, setPushFor] = useState(null); // trend item to push
  const [deleteFor, setDeleteFor] = useState(null);

  const canManage = can("marketing.manage") || can("brand.manage");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const params = new URLSearchParams({ tab });
      if (q) params.set("q", q);
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/trend-items?${params}`);
      setRows(data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't load trends"));
    } finally {
      setBusy(false);
    }
  }, [activeWorkspaceId, tab, q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/brands`).then(({ data }) => setBrands(data || [])).catch(() => {});
  }, [activeWorkspaceId]);

  const brandById = useMemo(() => {
    const m = new Map();
    brands.forEach((b) => m.set(b.id, b));
    return m;
  }, [brands]);

  const bookmarkToggle = useCallback(async (row) => {
    try {
      const { data } = await api.post(`/trend-items/${row.id}/bookmark`);
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, _i_bookmarked: data.bookmarked } : r));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't bookmark"));
    }
  }, []);

  const regenerateAngle = useCallback(async (row) => {
    try {
      toast.info("Regenerating content angle…");
      const { data } = await api.post(`/trend-items/${row.id}/regenerate-angle`);
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, content_angle: data.content_angle } : r));
      toast.success("Content angle refreshed");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Regeneration failed"));
    }
  }, []);

  const recomputeBrands = useCallback(async (row) => {
    try {
      const { data } = await api.post(`/trend-items/${row.id}/suggest-brands`);
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, suggested_brand_ids: data.suggested_brand_ids, suggested_brand_match_reason: data.reason } : r));
      toast.success(`Matched ${data.suggested_brand_ids.length} brand${data.suggested_brand_ids.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't recompute matches"));
    }
  }, []);

  const doDelete = useCallback(async () => {
    if (!deleteFor) return;
    try {
      await api.delete(`/trend-items/${deleteFor.id}`);
      setRows((prev) => prev.filter((r) => r.id !== deleteFor.id));
      toast.success("Idea deleted");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't delete"));
    } finally {
      setDeleteFor(null);
    }
  }, [deleteFor]);

  return (
    <>
      <PageHeader
        breadcrumb="Marketing"
        title="Trends & content bucket"
        description="A living library of content ideas — RSS-fed trends, hand-added inspiration, or bookmarks from around the web. Push any idea into a brand's social calendar with one click."
        testid="trends-header"
        actions={
          <div className="flex items-center gap-2">
            {canManage && (
              <Button variant="outline" className="rounded-none" onClick={() => setSettingsOpen(true)} data-testid="trends-settings-btn">
                <Settings className="w-3.5 h-3.5 mr-1.5" /> Settings
              </Button>
            )}
            <Button className="rounded-none" onClick={() => setAddOpen(true)} data-testid="trends-add-idea-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add idea
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 pt-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="inline-flex items-center gap-1 border border-border px-2 h-9 flex-1 max-w-md">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, description, tags…"
              className="flex-1 bg-transparent text-sm focus:outline-none"
              data-testid="trends-search"
            />
          </div>
          <span className="text-[11px] text-muted-foreground ml-auto">
            {rows.length} idea{rows.length === 1 ? "" : "s"}
          </span>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none h-10">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.key} value={t.key}
                  className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  data-testid={`trends-tab-${t.key}`}>
                  <Icon className="w-3.5 h-3.5 mr-1.5" /> {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-4">
              {busy && rows.length === 0 ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
              ) : rows.length === 0 ? (
                <EmptyState
                  title={t.key === "bookmarked" ? "No bookmarks yet" : t.key === "ideas" ? "No manual ideas yet" : t.key === "trending" ? "Nothing trending" : "Nothing here yet"}
                  description={t.key === "bookmarked" ? "Tap the bookmark on any card to save it here." : "Add your first idea, or wait for the RSS ingest to fill this up."}
                  icon={t.key === "trending" ? TrendingUp : Wand2}
                  testid="trends-empty"
                  action={<Button className="rounded-none" onClick={() => setAddOpen(true)}>Add idea</Button>}
                />
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="trends-grid">
                  {rows.map((row) => (
                    <TrendCard
                      key={row.id}
                      row={row}
                      brandById={brandById}
                      canManage={canManage}
                      onBookmark={() => bookmarkToggle(row)}
                      onRegenerate={() => regenerateAngle(row)}
                      onRecomputeBrands={() => recomputeBrands(row)}
                      onPush={() => setPushFor(row)}
                      onDelete={() => setDeleteFor(row)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {addOpen && (
        <AddIdeaDialog
          wsId={activeWorkspaceId}
          onClose={() => setAddOpen(false)}
          onCreated={(created) => {
            setAddOpen(false);
            setRows((prev) => [created, ...prev]);
          }}
        />
      )}
      {settingsOpen && (
        <TrendsSettingsDialog
          wsId={activeWorkspaceId}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      {pushFor && (
        <PushToBrandDialog
          row={pushFor}
          brands={brands}
          onClose={() => setPushFor(null)}
          onPushed={() => setPushFor(null)}
        />
      )}
      <AlertDialog open={!!deleteFor} onOpenChange={(o) => { if (!o) setDeleteFor(null); }}>
        <AlertDialogContent data-testid="trends-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete idea?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently remove &ldquo;{deleteFor?.title}&rdquo; from your workspace&rsquo;s trend library.
              Any content-calendar items already pushed from this idea will keep their content.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─────────────── Card ───────────────

function TrendCard({ row, brandById, canManage, onBookmark, onRegenerate, onRecomputeBrands, onPush, onDelete }) {
  const angle = row.content_angle || {};
  const suggested = (row.suggested_brand_ids || []).map((id) => brandById.get(id)).filter(Boolean);
  return (
    <div className={`border border-border bg-background hover:border-primary/60 transition p-3 flex flex-col gap-2 ${row.featured_today ? "ring-1 ring-amber-500" : ""}`} data-testid={`trend-card-${row.id}`}>
      {row.featured_today && (
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-600 font-semibold" data-testid={`trend-featured-badge-${row.id}`}>
          <Sparkles className="w-3 h-3" /> Featured today
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold leading-snug line-clamp-2" data-testid={`trend-card-title-${row.id}`}>
            {row.title}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 flex items-center gap-1.5">
            <span className="chip text-[10px]">{row.kind}</span>
            {row.source_name && <span className="inline-flex items-center gap-0.5"><Rss className="w-2.5 h-2.5" /> {row.source_name}</span>}
            <span>{fmtRelative(row.created_at)}</span>
          </div>
        </div>
        <button
          onClick={onBookmark}
          className={`p-1 shrink-0 hover:bg-muted transition ${row._i_bookmarked ? "text-primary" : "text-muted-foreground"}`}
          title={row._i_bookmarked ? "Remove bookmark" : "Bookmark"}
          data-testid={`trend-bookmark-${row.id}`}
        >
          {row._i_bookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
        </button>
      </div>

      {row.image_url && (
        <a href={row.url || row.image_url} target="_blank" rel="noreferrer" className="block">
          <img src={row.image_url} alt="" className="w-full h-32 object-cover" loading="lazy" />
        </a>
      )}

      {row.description && (
        <p className="text-[12px] text-muted-foreground line-clamp-3">{row.description}</p>
      )}

      {(row.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {row.tags.slice(0, 6).map((t) => (
            <span key={t} className="chip text-[10px]">#{t}</span>
          ))}
        </div>
      )}

      {/* AI content angle */}
      <div className="border-l-2 border-primary/60 pl-2 bg-primary/5 text-[11px] py-1.5" data-testid={`trend-angle-${row.id}`}>
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-primary font-semibold mb-0.5">
          <Sparkles className="w-2.5 h-2.5" /> Content angle
          <button
            onClick={onRegenerate}
            className="ml-auto text-muted-foreground hover:text-foreground"
            title="Regenerate"
            data-testid={`trend-regenerate-${row.id}`}
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
        {angle.headline && <div className="font-semibold text-foreground leading-snug">{angle.headline}</div>}
        {angle.hook && <div className="text-muted-foreground italic mt-0.5 line-clamp-2">&ldquo;{angle.hook}&rdquo;</div>}
      </div>

      {/* Suggested brands */}
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Fits:</span>
        {suggested.length === 0 ? (
          <span className="text-[10px] text-muted-foreground italic">no brand matches</span>
        ) : (
          suggested.slice(0, 4).map((b) => (
            <span key={b.id} className="chip text-[10px]">{b.name}</span>
          ))
        )}
        {suggested.length > 4 && <span className="text-[10px] text-muted-foreground">+{suggested.length - 4}</span>}
        <button
          onClick={onRecomputeBrands}
          className="ml-auto text-muted-foreground hover:text-foreground text-[10px] uppercase tracking-widest"
          title="Recompute brand matches"
          data-testid={`trend-rematch-${row.id}`}
        >
          <Zap className="w-3 h-3" />
        </button>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 mt-1">
        {canManage && (
          <Button size="sm" className="rounded-none h-8 flex-1" onClick={onPush} data-testid={`trend-push-${row.id}`}>
            <Send className="w-3 h-3 mr-1" /> Push to brand
          </Button>
        )}
        {row.url && (
          <Button size="sm" variant="outline" className="rounded-none h-8" asChild>
            <a href={row.url} target="_blank" rel="noreferrer" title="Open source" onClick={(e) => e.stopPropagation()}>
              <ExternalLink className="w-3 h-3" />
            </a>
          </Button>
        )}
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={onDelete} title="Delete" data-testid={`trend-delete-${row.id}`}>
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

// ─────────────── Add-idea dialog ───────────────

function AddIdeaDialog({ wsId, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [image, setImage] = useState("");
  const [tags, setTags] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/trend-items`, {
        title, description, url, image_url: image, category,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        kind: "idea",
      });
      toast.success("Idea saved · AI angle drafted");
      onCreated(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't save idea"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-none" data-testid="trend-add-dialog">
        <DialogHeader><DialogTitle>Add a content idea</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Diwali 2026 gifting reels" data-testid="trend-add-title" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="What's this idea about?" data-testid="trend-add-desc" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Source URL</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid="trend-add-url" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Image URL</Label>
              <Input value={image} onChange={(e) => setImage(e.target.value)} placeholder="https://…" data-testid="trend-add-image" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Category</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="festival, product, tutorial…" data-testid="trend-add-cat" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Tags (comma-separated)</Label>
              <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="diwali, fashion, gifting" data-testid="trend-add-tags" />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground italic">
            The AI will auto-draft a content angle (headline · hook · captions) and suggest matching brands on save.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="trend-add-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="trend-add-save">
            {busy ? "Drafting…" : "Save idea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Push dialog ───────────────

function PushToBrandDialog({ row, brands, onClose, onPushed }) {
  const suggestedFirst = useMemo(() => {
    const sid = new Set(row.suggested_brand_ids || []);
    const sug = brands.filter((b) => sid.has(b.id));
    const rest = brands.filter((b) => !sid.has(b.id));
    return [...sug, ...rest];
  }, [brands, row.suggested_brand_ids]);
  const [brandId, setBrandId] = useState(suggestedFirst[0]?.id || "");
  const [plannedDate, setPlannedDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!brandId) return toast.error("Select a brand");
    setBusy(true);
    try {
      const { data } = await api.post(`/trend-items/${row.id}/push-to-brand`, {
        brand_id: brandId, planned_date: plannedDate,
      });
      const suffix = data.task_ids?.length ? ` + ${data.task_ids.length} task${data.task_ids.length === 1 ? "" : "s"}` : "";
      toast.success(`Pushed to ${data.brand.name} · draft post created${suffix}`);
      onPushed();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Push failed"));
    } finally { setBusy(false); }
  };

  const isSuggested = (row.suggested_brand_ids || []).includes(brandId);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-none" data-testid="trend-push-dialog">
        <DialogHeader><DialogTitle>Push idea to brand</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="border border-primary/30 bg-primary/5 p-2 text-xs">
            <div className="font-semibold line-clamp-2">{row.title}</div>
            {row.content_angle?.headline && <div className="text-muted-foreground mt-0.5 line-clamp-2">{row.content_angle.headline}</div>}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Brand</Label>
            <Select value={brandId} onValueChange={setBrandId}>
              <SelectTrigger className="h-9 rounded-none" data-testid="trend-push-brand"><SelectValue placeholder="Choose a brand" /></SelectTrigger>
              <SelectContent>
                {suggestedFirst.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name} {(row.suggested_brand_ids || []).includes(b.id) && <span className="text-[10px] text-primary">✨</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isSuggested && (
              <div className="text-[10px] text-primary mt-1">AI suggested this brand ({row.suggested_brand_match_reason || "industry match"})</div>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Planned date</Label>
            <Input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} className="h-9 rounded-none" data-testid="trend-push-date" />
          </div>
          <p className="text-[10px] text-muted-foreground italic">
            This creates a draft social-calendar post + a task assigned to the brand&rsquo;s content owner.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || !brandId} data-testid="trend-push-submit">
            <Send className="w-3 h-3 mr-1" /> {busy ? "Pushing…" : "Push"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Settings dialog ───────────────

function TrendsSettingsDialog({ wsId, onClose }) {
  const [semantic, setSemantic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState([]);
  const [newFeedName, setNewFeedName] = useState("");
  const [newFeedUrl, setNewFeedUrl] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [deleteFeed, setDeleteFeed] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/workspaces/${wsId}/trends/settings`).then(({ data }) => setSemantic(!!data.semantic_brand_match)),
      api.get(`/workspaces/${wsId}/trend-sources`).then(({ data }) => setSources(data || [])),
    ]).finally(() => setLoading(false));
  }, [wsId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${wsId}/trends/settings`, { semantic_brand_match: semantic });
      toast.success("Settings saved");
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };

  const addFeed = async () => {
    if (!newFeedName.trim() || !newFeedUrl.trim()) return toast.error("Name and URL required");
    try {
      const { data } = await api.post(`/workspaces/${wsId}/trend-sources`, {
        name: newFeedName, url: newFeedUrl, kind: "rss", enabled: true,
      });
      setSources((prev) => [data, ...prev]);
      setNewFeedName(""); setNewFeedUrl("");
      toast.success("Feed added");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't add feed"));
    }
  };

  const toggleFeed = async (s) => {
    try {
      const { data } = await api.patch(`/trend-sources/${s.id}`, { enabled: !s.enabled });
      setSources((prev) => prev.map((x) => x.id === s.id ? data : x));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Toggle failed"));
    }
  };

  const removeFeed = async (s) => {
    // Iter 148 followup — replaced browser-native window.confirm with
    // shadcn AlertDialog for design parity with the rest of the page.
    if (!s) return;
    try {
      await api.delete(`/trend-sources/${s.id}`);
      setSources((prev) => prev.filter((x) => x.id !== s.id));
      toast.success("Feed removed");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally { setDeleteFeed(null); }
  };

  const ingestNow = async (sourceId) => {
    setIngesting(true);
    try {
      const url = sourceId
        ? `/workspaces/${wsId}/trend-sources/ingest-now?source_id=${sourceId}`
        : `/workspaces/${wsId}/trend-sources/ingest-now`;
      const { data } = await api.post(url);
      toast.success(`Pulled ${data.new || 0} new · ${data.skipped || 0} dup${data.errors ? ` · ${data.errors} err` : ""}`);
      // Refresh sources to show last_pulled_at.
      const { data: fresh } = await api.get(`/workspaces/${wsId}/trend-sources`);
      setSources(fresh || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Ingest failed"));
    } finally { setIngesting(false); }
  };

  const sendRecapPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/trends/weekly-recap/trigger`);
      if (data?.sent) toast.success(`Preview recap sent to ${data.to}`);
      else toast.info(data?.note || "No trends this week yet");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Preview failed"));
    } finally { setPreviewing(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="trends-settings-dialog">
        <DialogHeader><DialogTitle>Trends settings</DialogTitle></DialogHeader>
        {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
          <div className="space-y-5">
            {/* Semantic matching switch */}
            <section className="border border-border p-3">
              <div className="flex items-start gap-3">
                <Switch checked={semantic} onCheckedChange={setSemantic} data-testid="trends-semantic-switch" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Semantic brand matching</div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    When ON, Claude expands brand matching beyond exact industry tags — e.g. &ldquo;Diwali 2026&rdquo; auto-suggests fashion, retail, food brands. When OFF (default), only strict industry / sub-industry matches are used.
                  </p>
                </div>
              </div>
            </section>

            {/* RSS sources */}
            <section className="border border-border" data-testid="trends-sources-section">
              <div className="px-3 py-2 bg-muted/40 border-b border-border flex items-center gap-2 text-xs">
                <Rss className="w-3.5 h-3.5 text-primary" />
                <span className="font-semibold uppercase tracking-widest">RSS feeds</span>
                <span className="normal-case text-[10px] text-muted-foreground">{sources.filter((s) => s.kind === "rss").length} feeds · nightly pull at 03:00 UTC</span>
                <div className="ml-auto flex gap-1">
                  <Button size="sm" variant="outline" className="rounded-none h-7 px-2 text-[11px]" onClick={() => ingestNow(null)} disabled={ingesting} data-testid="trends-ingest-all">
                    <RefreshCw className={`w-3 h-3 mr-1 ${ingesting ? "animate-spin" : ""}`} /> Pull all
                  </Button>
                </div>
              </div>
              <div className="p-3 space-y-2">
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <Label className="text-[10px] uppercase tracking-widest">Name</Label>
                    <Input value={newFeedName} onChange={(e) => setNewFeedName(e.target.value)} placeholder="Marketing Brew" data-testid="trends-feed-name" />
                  </div>
                  <div className="col-span-6">
                    <Label className="text-[10px] uppercase tracking-widest">Feed URL</Label>
                    <Input value={newFeedUrl} onChange={(e) => setNewFeedUrl(e.target.value)} placeholder="https://example.com/feed" data-testid="trends-feed-url" />
                  </div>
                  <div className="col-span-2">
                    <Button className="rounded-none w-full" onClick={addFeed} data-testid="trends-feed-add">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add
                    </Button>
                  </div>
                </div>
                {sources.filter((s) => s.kind === "rss").length === 0 ? (
                  <div className="text-[11px] text-muted-foreground italic py-2">
                    No RSS feeds yet. Add one above — try any blog&rsquo;s /feed URL.
                  </div>
                ) : (
                  <div className="border border-border divide-y divide-border" data-testid="trends-feed-list">
                    {sources.filter((s) => s.kind === "rss").map((s) => (
                      <div key={s.id} className="px-2 py-2 text-xs flex items-center gap-2" data-testid={`trends-feed-row-${s.id}`}>
                        <Switch checked={!!s.enabled} onCheckedChange={() => toggleFeed(s)} data-testid={`trends-feed-toggle-${s.id}`} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{s.name}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{s.url}</div>
                        </div>
                        <div className="text-[10px] text-muted-foreground w-40 text-right">
                          {s.last_pulled_at ? `pulled ${fmtRelative(s.last_pulled_at)}` : "never pulled"}
                          <div className="text-[9px]">{s.item_count || 0} items</div>
                        </div>
                        <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => ingestNow(s.id)} disabled={ingesting} title="Pull now" data-testid={`trends-feed-ingest-${s.id}`}>
                          <RefreshCw className={`w-3 h-3 ${ingesting ? "animate-spin" : ""}`} />
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive" onClick={() => setDeleteFeed(s)} title="Delete" data-testid={`trends-feed-delete-${s.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Weekly recap preview */}
            <section className="border border-border p-3 flex items-center gap-3">
              <div className="flex-1">
                <div className="text-sm font-semibold">Weekly Trends recap</div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Every Friday 09:00 UTC, we email brand managers a digest of the top 10 bookmarked + 5 unpushed ideas. Send yourself a preview to see what it looks like.
                </p>
              </div>
              <Button variant="outline" className="rounded-none" onClick={sendRecapPreview} disabled={previewing} data-testid="trends-recap-preview">
                {previewing ? "Sending…" : "Email preview"}
              </Button>
            </section>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy || loading} data-testid="trends-settings-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <AlertDialog open={!!deleteFeed} onOpenChange={(o) => { if (!o) setDeleteFeed(null); }}>
        <AlertDialogContent data-testid="trends-feed-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove RSS feed?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{deleteFeed?.name}&rdquo; will stop being polled. Existing ideas already ingested from this feed remain in your library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trends-feed-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => removeFeed(deleteFeed)} data-testid="trends-feed-delete-ok">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
